import axios from "../../../axios";
import { useEffect, useState } from "react";
import CloseIcon from "../../../assets/svgs/close.svg";
import styles from "./AddAccount.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { ADD_ACCOUNT, ADD_NOTIFICATION, UPDATE_ACCOUNT } from "../../../store/types";
import classNames from "classnames";
import { Checkbox, FormControlLabel, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { ACCOUNT_PERCENTAGE_BY, PAGE_TYPE } from "../../../types/pages.type";
import { LoadingButton } from "@mui/lab";
import { AUTH_ROLES } from "../../../types/auth.type";
import _ from "lodash";

const DASHBOARD_OPTIONS = [
    { label: "Hospital dashboard access", type: PAGE_TYPE.HOSPITAL },
    { label: "Community transfer dashboard access", type: PAGE_TYPE.COMMUNITY_TRANSFER },
    { label: "Deceased dashboard access", type: PAGE_TYPE.DECEASED },
    { label: "Admission dashboard access", type: PAGE_TYPE.ADMISSION },
    { label: "Overall dashboard access", type: PAGE_TYPE.OVERALL },
];

const AddAccount = props => {
    const dispatch = useDispatch();
    const [name, setName] = useState("");
    const [dashboardAccess, setDashboardAccess] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [sourceAccountId, setSourceAccountId] = useState("");
    const [allAccounts, setAllAccounts] = useState([]);
    const [showQuestionSelection, setShowQuestionSelection] = useState(false);
    const [cloneableQuestions, setCloneableQuestions] = useState([]);
    const [cloneableTabs, setCloneableTabs] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [selectedTabs, setSelectedTabs] = useState([]);
    const [isFetchingQuestions, setIsFetchingQuestions] = useState(false);

    const { auth } = useSelector((state) => state);
    const isSuperAdmin = auth?.role?.slug === AUTH_ROLES.SUPER;

    const save = async () => {
        try {
            setIsError(false)
            if (!name || (isDuplicate && !sourceAccountId)) {
                setIsError(true)
                return
            }

            if (isDuplicate && sourceAccountId && !showQuestionSelection) {
                setIsFetchingQuestions(true);
                setIsLoading(true);
                try {
                    const res = await axios.get(`/api/account/duplicate-questions/${sourceAccountId}`);
                    const { questions = [], tabs = [] } = res.data || {};
                    setCloneableQuestions(questions);
                    setCloneableTabs(tabs);
                    setSelectedQuestions(questions.map(q => ({ _id: q._id.toString(), isUniversal: false, selected: true })));
                    setSelectedTabs(tabs.map(t => ({ _id: t._id.toString(), isUniversal: false, selected: true })));
                    setShowQuestionSelection(true);
                } catch (error) {
                    console.error("Error fetching cloneable questions:", error);
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: "error",
                            label: "Failed to fetch cloneable questions.",
                            id: 'fetchQuestionsError'
                        },
                    });
                }
                setIsFetchingQuestions(false);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            if (isEdit) {
                let re = await axios.put("/api/account/" + props.currentEdit._id, {
                    name,
                });
                if (re && props.accountUpdated) {
                    props.accountUpdated(re.data);
                    dispatch({
                        type: UPDATE_ACCOUNT,
                        payload: re.data,
                    });
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: "success",
                            label: "Account data updated successfully!",
                            id: "accountDataUpdated",
                        },
                    });
                }
                props.clearCurrentEdit && props.clearCurrentEdit();
            } else {
                const re = await axios.post("/api/account", {
                    name,
                    isDuplicate,
                    sourceAccountId,
                    selectedQuestions: selectedQuestions.filter(q => q.selected).map(q => ({ _id: q._id, isUniversal: q.isUniversal })),
                    selectedTabs: selectedTabs.filter(t => t.selected).map(t => ({ _id: t._id, isUniversal: t.isUniversal })),
                    ...!isEdit && ({ dashboardAccess: dashboardAccess ? dashboardAccess : [] }),
                    ...!isEdit && ({ percentageBy: dashboardAccess?.length !== DASHBOARD_OPTIONS.length ? ACCOUNT_PERCENTAGE_BY.BED : null })
                });

                if (re.data.taskId) {
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: "info",
                            label: "Account created! Now duplicating settings in background...",
                            id: 'cloningStarted'
                        },
                    });

                    const pollTask = async (taskId) => {
                        try {
                            const statusRe = await axios.get(`/api/account/cloning-status/${taskId}`);
                            const { status, result, error } = statusRe.data;
                            if (status === 'complete') {
                                if (result) {
                                    if (props.accountAdded) {
                                        props.accountAdded(result);
                                    }
                                    dispatch({
                                        type: ADD_ACCOUNT,
                                        payload: result,
                                    });
                                }
                                dispatch({
                                    type: ADD_NOTIFICATION,
                                    payload: {
                                        type: "success",
                                        label: "Account settings duplicated successfully!",
                                        id: 'cloningStarted'
                                    },
                                });
                            } else if (status === 'failed') {
                                dispatch({
                                    type: ADD_NOTIFICATION,
                                    payload: {
                                        type: "error",
                                        label: error || "Duplication failed.",
                                        id: 'cloningStarted'
                                    },
                                });
                            } else {
                                setTimeout(() => pollTask(taskId), 3000);
                            }
                        } catch (e) {
                            console.error("Cloning poll error:", e);
                        }
                    };

                    setIsLoading(false);
                    props.close();
                    pollTask(re.data.taskId);
                    return;
                }

                if (re && props.accountAdded) {
                    props.accountAdded(re.data);
                }

                dispatch({
                    type: ADD_ACCOUNT,
                    payload: re.data,
                });

                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: {
                        type: "success",
                        label: "Account added successfully!",
                        id: 'accountAdded'
                    },
                });
            }
            setIsLoading(false);
            props.close();
        } catch (e) {
            setIsLoading(false);
            dispatch({
                type: ADD_NOTIFICATION,
                payload: {
                    type: "error",
                    label: e?.response?.data?.message || "Error while trying to add account.",
                    id: 'addAccountError'
                },
            });
        }
    };

    const toggleQuestionSelection = (id) => {
        setSelectedQuestions(prev => prev.map(q => q._id.toString() === id.toString() ? { ...q, selected: !q.selected } : q));
    };

    const toggleUniversal = (id) => {
        setSelectedQuestions(prev => prev.map(q => q._id.toString() === id.toString() ? { ...q, isUniversal: !q.isUniversal } : q));
    };

    const toggleTabSelection = (id) => {
        setSelectedTabs(prev => prev.map(t => t._id.toString() === id.toString() ? { ...t, selected: !t.selected } : t));
    };

    const toggleTabUniversal = (id) => {
        setSelectedTabs(prev => prev.map(t => t._id.toString() === id.toString() ? { ...t, isUniversal: !t.isUniversal } : t));
    };

    const toggleUniversalForAll = () => {
        const anyUniversal = selectedQuestions.some(q => q.selected && q.isUniversal) || selectedTabs.some(t => t.selected && t.isUniversal);
        setSelectedQuestions(prev => prev.map(q => q.selected ? { ...q, isUniversal: !anyUniversal } : q));
        setSelectedTabs(prev => prev.map(t => t.selected ? { ...t, isUniversal: !anyUniversal } : t));
    };

    useEffect(() => {
        if (props.currentEdit && props.currentEdit._id) {
            setIsEdit(true);
            setName(props.currentEdit.name);
        }
    }, [props.currentEdit]);

    useEffect(() => {
        const fetchAccounts = async () => {
            if (isSuperAdmin && !isEdit) {
                try {
                    const res = await axios.get("/api/account/all");
                    setAllAccounts(_.orderBy(res.data || [], ["name"], ["asc"]));
                } catch (error) {
                    console.error("Error fetching accounts:", error);
                }
            }
        };
        fetchAccounts();
    }, [isSuperAdmin, isEdit]);

    return (
        <>
            <div className={styles.overlay}></div>
            <div className={styles.addPopup}>
                <div className={classNames("df aic", styles.hdr)}>
                    <p className={`ffmm fs18`}>{isEdit ? "Edit" : "Add"} account</p>
                    <div className={`mla`}>
                        <div
                            className={classNames("df acc", styles.closeBtn)}
                            onClick={() => {
                                props.close();
                                props.clearCurrentEdit();
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>

                </div>{" "}
                <div className={styles.popupCntnt}>
                    <div className={`m-b-15 inputWrpr`}>
                        <label className={`ffmr fs12 ${isError && !name ? "error" : null}`}>Account Name</label>
                        <input
                            style={{
                                ...(isError && !name && { borderColor: "red" }),
                            }}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    {!isEdit && isSuperAdmin && (
                        <div className="m-b-15">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isDuplicate}
                                        onChange={(e) => {
                                            setIsDuplicate(e.target.checked);
                                            if (!e.target.checked) setSourceAccountId("");
                                            setCloneableQuestions([]);
                                            setCloneableTabs([]);
                                            setSelectedQuestions([]);
                                            setSelectedTabs([]);
                                            setShowQuestionSelection(false);
                                        }}
                                    />
                                }
                                label="Duplicate settings from existing account"
                                slotProps={{
                                    typography: {
                                        sx: {
                                            fontSize: "13px",
                                            fontFamily: "mont reg",
                                        },
                                    },
                                }}
                            />
                            {isDuplicate && (
                                <div className="m-t-8">
                                    <Autocomplete
                                        fullWidth
                                        options={allAccounts}
                                        getOptionLabel={(option) => option.name || ""}
                                        value={allAccounts.find(acc => acc._id === sourceAccountId) || null}
                                        onChange={(event, newValue) => {
                                            setSourceAccountId(newValue ? newValue._id : "");
                                            setCloneableQuestions([]);
                                            setCloneableTabs([]);
                                            setSelectedQuestions([]);
                                            setSelectedTabs([]);
                                            setShowQuestionSelection(false);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Source Account"
                                                variant="outlined"
                                                error={isError && isDuplicate && !sourceAccountId}
                                                sx={{
                                                    '& .MuiInputLabel-root': {
                                                        fontFamily: 'mont reg',
                                                        fontSize: '14px',
                                                        ...(isError && isDuplicate && !sourceAccountId && { color: 'red' })
                                                    },
                                                    '& .MuiOutlinedInput-root': {
                                                        fontFamily: 'mont reg',
                                                        fontSize: '14px',
                                                        ...(isError && isDuplicate && !sourceAccountId && { '& fieldset': { borderColor: 'red' } })
                                                    }
                                                }}
                                            />
                                        )}
                                        sx={{
                                            '& .MuiAutocomplete-option': {
                                                fontFamily: 'mont reg',
                                                fontSize: '14px'
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {!isEdit && !isDuplicate && (
                        DASHBOARD_OPTIONS.map(({ label, type }) => (
                            <div key={type} className="m-b-8 inputWrpr">
                                <FormControlLabel
                                    label={label}
                                    className="ffmr fs12"
                                    slotProps={{
                                        typography: {
                                            sx: {
                                                mt: "10px",
                                                fontSize: "13px",
                                                fontFamily: "mont reg",
                                            },
                                        },
                                    }}
                                    control={
                                        <Checkbox
                                            checked={_.includes(dashboardAccess, type)}
                                            onChange={(e) =>
                                                setDashboardAccess(
                                                    e.target.checked
                                                        ? [...dashboardAccess, type]
                                                        : _.without(dashboardAccess, type)
                                                )
                                            }
                                        />
                                    }
                                />
                            </div>
                        ))
                    )}
                    {(showQuestionSelection || isFetchingQuestions) && (
                        <div className={styles.questionList}>
                            {isFetchingQuestions ? (
                                <div className="df acc jcc p-v-40">
                                    <CircularProgress size={24} />
                                </div>
                            ) : (cloneableQuestions.length === 0 && cloneableTabs.length === 0) ? (
                                <p className="ffmr fs13 tac p-v-40" style={{ color: "#666", opacity: 0.8 }}>No custom questions or tabs found.</p>
                            ) : (
                                <>
                                    <div className="m-b-20">
                                        <p className="ffmm fs14 m-b-8">Select Items to Duplicate</p>
                                        <p
                                            className="ffmr fs12 cp m-t-12"
                                            style={{ color: '#2196f3', textDecoration: 'underline', width: 'fit-content' }}
                                            onClick={toggleUniversalForAll}
                                        >
                                            {(selectedQuestions.some(q => q.selected && q.isUniversal) || selectedTabs.some(t => t.selected && t.isUniversal))
                                                ? "Disable Universal for All"
                                                : "Enable Universal for All"}
                                        </p>
                                    </div>
                                    <div className={styles.scrollArea}>
                                        {[
                                            ...cloneableQuestions.map(q => ({ ...q, itemType: 'question' })),
                                            ...cloneableTabs.map(t => ({ ...t, itemType: 'tab' }))
                                        ].map((item) => {
                                            const isQuestion = item.itemType === 'question';
                                            const selection = isQuestion
                                                ? selectedQuestions.find(sq => sq._id.toString() === item._id.toString())
                                                : selectedTabs.find(st => st._id.toString() === item._id.toString());

                                            const typeLabel = isQuestion
                                                ? "(Custom Question)"
                                                : item.type === "combineTab" ? "(Combine Tab)" : "(Custom Tab)";

                                            const secondaryText = isQuestion
                                                ? (item.forType === 'admission' ? 'Admissions' : item.forType === 'return' ? 'Return' : {
                                                    safeDischarge: 'Safe Discharge',
                                                    SNF: 'SNF',
                                                    AMA: 'AMA',
                                                    totalCommunity: 'Total Community',
                                                    deceased: 'Deceased',
                                                    hospitalTransfer: 'Hospital Transfer'
                                                }[item.forTransferType] || item.forTransferType)
                                                : {
                                                    hospital: 'Hospital',
                                                    communityTransfer: 'Community Transfer',
                                                    deceased: 'Deceased',
                                                    admission: 'Admission',
                                                    overall: 'Overall',
                                                    adt: 'ADT'
                                                }[item.page] || item.page;

                                            return (
                                                <div key={item._id} className={classNames("df aic jcsb m-b-10", styles.questionItem)}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={selection?.selected}
                                                                onChange={() => isQuestion ? toggleQuestionSelection(item._id) : toggleTabSelection(item._id)}
                                                            />
                                                        }
                                                        label={
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                <span className="ffmm fs13" style={{ lineHeight: '1.2' }}>
                                                                    {item.label} <span style={{ fontSize: '11px', color: '#888', marginLeft: '4px' }}>{typeLabel}</span>
                                                                </span>
                                                                {secondaryText && (
                                                                    <span className="ffmr fs11" style={{ color: '#999', marginTop: '2px' }}>
                                                                        {secondaryText}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        }
                                                    />
                                                    {selection?.selected && (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={selection?.isUniversal}
                                                                    onChange={() => isQuestion ? toggleUniversal(item._id) : toggleTabUniversal(item._id)}
                                                                    color="secondary"
                                                                />
                                                            }
                                                            label="Universal"
                                                            slotProps={{
                                                                typography: { sx: { fontSize: "12px", fontFamily: "mont reg", color: "#666" } }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className={`${styles.popupFtr}`}>
                    <div className={classNames("df aic", styles.actionsWrrp)}>
                        <div className={`mla`}>
                            <button
                                className={classNames("ffmr fs14 m-r-8", styles.text)}
                                onClick={() => {
                                    props.close();
                                    props.clearCurrentEdit && props.clearCurrentEdit();
                                }}
                            >
                                Cancel
                            </button>
                            <LoadingButton
                                variant="contained"
                                className={classNames("ffmr fs14")}
                                onClick={save}
                                loading={isLoading}
                            >
                                {isDuplicate && !showQuestionSelection ? "Continue" : "Save"}
                            </LoadingButton>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddAccount;
